import { ArrowBack } from "@mui/icons-material";
import { Box, Button, CircularProgress, Collapse, FormControl, FormControlLabel, FormLabel, Grow, IconButton, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import DOMPurify from 'dompurify';
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from '../../assets/logos/icare_acrologo.png';
import Footer1 from "../../components/nav/Footer1";
import { userFeedback } from "../../consts/feedback";
import { sendReactGAPageView } from "../../consts/helpers";
import useAuthUser from "../../hooks/useAuthUser";

export default function FeedbackFormPage() {
	const { pathname } = useLocation();
	useEffect(() => {
        sendReactGAPageView(pathname, 'Feedback Form');
    }, [pathname])

    var { userid }  = useAuthUser();
    const navigate = useNavigate();
    
    // Inputs
    const [feedbackInputs, setFInputs] = useState({});

    // Flags
    const [isReadySubmit, setReady] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const [isSubmitSuccess, setSuccess] = useState(false);

    const handleBackButtonClick = () => {
        navigate('/feedback');
    }
    
    const handleInput = (event) => {
        setFInputs(i => ({
            ...i,
            [event.target.name]: event.target.value
        }));
    };

    const handleSubmit = () => {
        setSubmitting(true);
        const body = {
            uid: userid,
            feedbackInputs: feedbackInputs,
        }

        const url = process.env.REACT_APP_FEEDBACK_LAMBDA;
        fetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then(response => {
            return response.json();
        }).then(data => {
            // Set display flags
            setSubmitting(false);
            setSuccess(data?.isSuccess);
        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error);
            return {};
        });
    }

    const sanitizedText = (text) => ({
        __html: DOMPurify.sanitize(text)
    })

    useEffect(() => {
        setReady(Object.values(feedbackInputs).filter(x => x).length === Object.keys(userFeedback).length)
    }, [feedbackInputs])

    return (
        <>
            <Stack direction={'row'} spacing={1} sx={{ pt: '32px' }}>
                <IconButton onClick={handleBackButtonClick}>
                    <ArrowBack />
                </IconButton>
                <Box>
                    <img src={logo} alt='icare app logo' style={{ maxWidth: 100 }}/>
                </Box>
            </Stack>

            <Box sx={{ flex: 'auto', py: 2 }} className='spread-y'>
                <Stack spacing={2}>
                <Typography variant="h6">User Feedback Form</Typography>
                {
                    isSubmitting &&
                    <Box className='centered-container'>
                        <CircularProgress color='secondary' />
                    </Box>
                }
                <Collapse in={!isSubmitSuccess && !isSubmitting}>
                    <Stack spacing={2}>
                    {
                        Object.entries(userFeedback).map(([key, item], id) => {
                            return (
                                <FormControl key={id}>
                                    <FormLabel id={`qn${id}-label`}>{id+1}. { item.qn }<span style={{ color: 'red' }}> *</span></FormLabel>
                                    { (item?.options) ?
                                        <RadioGroup
                                            aria-labelledby={`qn${id}-label`}
                                            name={item.qn}
                                            value={feedbackInputs?.[item.qn] || ''}
                                            onChange={handleInput}
                                        >
                                            {(item.options).map(( opt, oid ) => {
                                                return <FormControlLabel key={oid} value={opt.value} control={<Radio />} label={<span dangerouslySetInnerHTML={sanitizedText(opt.label)} />} />
                                            })}
                                        </RadioGroup>
                                        :
                                        <TextField 
                                            aria-labelledby={`qn${id}-label`}
                                            name={item.qn} 
                                            onChange={handleInput} 
                                            multiline 
                                            rows={5} 
                                        />
                                    }
                                </FormControl>
                            )
                        })
                    }
                    
                    <Typography variant="caption" sx={{ color: 'red' }}>*: Indicates a required question</Typography>

                    <Button className={`pill_btn ${(!isReadySubmit) ? 'Mui-disabled' : 'pri_btn'}`} disabled={!isReadySubmit} onClick={handleSubmit} sx={{ mt: 2 }} fullWidth>submit</Button>
                    </Stack>
                </Collapse>
                <Grow in={isSubmitSuccess}>
                <Box>
                    <Typography variant="body2">Thank you for your feedback! We appreciate your input and will use it to continue improving your experience.</Typography>
                    <Button className={'pill_btn pri_btn'} onClick={handleBackButtonClick} sx={{ mt: 2 }} fullWidth>back to homepage</Button>
                </Box>
                </Grow>
                </Stack>
                <Footer1 />
            </Box>
        </>
    )
}